import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import Modal from "../../components/Modal";
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import { updateMerchantKyb } from "../../services/users";
import { uploadKybFile } from "../../utils/kyb-upload";

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const normalizePersonName = (value) =>
  `${value || ""}`.toLowerCase().replace(/\s+/g, " ").trim();

const expectedMdName = (firstName, lastName) =>
  normalizePersonName(`${firstName || ""} ${lastName || ""}`);

const toDateInput = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const emptyForm = (user) => ({
  business_registration_number: user?.merchant_kyb?.business_registration_number || "",
  business_registration_expiry: toDateInput(user?.merchant_kyb?.business_registration_expiry),
  business_registration_doc: user?.merchant_kyb?.business_registration_doc || "",
  md_name:
    user?.merchant_kyb?.md_name ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
  md_id_expiry: toDateInput(user?.merchant_kyb?.md_id_expiry),
  md_selfie_doc: user?.merchant_kyb?.md_selfie_doc || "",
  poa_document_date: toDateInput(user?.merchant_kyb?.poa_document_date),
  proof_of_address_doc: user?.merchant_kyb?.proof_of_address_doc || "",
  tax_id: user?.merchant_kyb?.tax_id || "",
  status: user?.merchant_kyb?.status || "PENDING",
});

const MerchantKybModal = forwardRef(({ authToken, onSaved }, ref) => {
  const modalRef = useRef(null);
  const authTokenRef = useRef(authToken);
  authTokenRef.current = authToken;
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isUploading = Object.values(uploading).some(Boolean);

  useImperativeHandle(ref, () => ({
    open: (merchant) => {
      setUser(merchant);
      setForm(emptyForm(merchant));
      setUploading({});
      setError("");
      modalRef.current.open();
    },
  }));

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const uploadKybDocument = (file, docType, fieldName) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [fieldName]: true }));
    uploadKybFile(file, docType)
      .then((url) => {
        updateField(fieldName, url);
      })
      .catch((err) => {
        console.error("KYB upload failed", err);
        toast.error(
          err?.message && !`${err.message}`.includes("Firebase")
            ? err.message
            : "Failed to upload KYB document, please try again."
        );
      })
      .finally(() => {
        setUploading((prev) => ({ ...prev, [fieldName]: false }));
      });
  };

  const validateForm = () => {
    const required = [
      "business_registration_number",
      "business_registration_expiry",
      "business_registration_doc",
      "md_name",
      "md_id_expiry",
      "md_selfie_doc",
      "poa_document_date",
      "proof_of_address_doc",
      "tax_id",
    ];
    if (required.some((key) => !`${form[key] || ""}`.trim())) {
      return "Provide business registration, MD selfie ID, proof of address, and tax ID.";
    }

    const today = startOfToday();
    const regExpiry = new Date(form.business_registration_expiry);
    const mdExpiry = new Date(form.md_id_expiry);
    const poaDate = new Date(form.poa_document_date);
    [regExpiry, mdExpiry, poaDate].forEach((date) => date.setHours(0, 0, 0, 0));

    if (Number.isNaN(regExpiry.getTime()) || regExpiry < today) {
      return "Business registration expiry must be today or later.";
    }
    if (Number.isNaN(mdExpiry.getTime()) || mdExpiry < today) {
      return "MD ID expiry must be today or later.";
    }
    const minPoa = new Date(today);
    minPoa.setDate(minPoa.getDate() - 90);
    if (Number.isNaN(poaDate.getTime()) || poaDate < minPoa || poaDate > today) {
      return "Proof of address must be dated within the last 90 days.";
    }

    const expected = expectedMdName(user?.first_name, user?.last_name);
    if (!expected || normalizePersonName(form.md_name) !== expected) {
      return "MD name must match the merchant's first and last name.";
    }
    return "";
  };

  const handleSave = () => {
    if (saving || isUploading || !user?.id) return;
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");
    updateMerchantKyb(
      user.id,
      {
        business_registration_number: form.business_registration_number.trim(),
        business_registration_expiry: form.business_registration_expiry,
        business_registration_doc: form.business_registration_doc,
        md_name: form.md_name.trim(),
        md_id_expiry: form.md_id_expiry,
        md_selfie_doc: form.md_selfie_doc,
        poa_document_date: form.poa_document_date,
        proof_of_address_doc: form.proof_of_address_doc,
        tax_id: form.tax_id.trim(),
        status: form.status,
      },
      authTokenRef.current
    )
      .then((res) => {
        if (res.status_code == 200) {
          toast.success(`${res.message}`);
          modalRef.current.close();
          onSaved?.();
        } else {
          setError(res.message || "Could not update KYB");
          toast.error(`${res.message}`);
        }
      })
      .catch((err) => {
        setError("Could not update KYB");
        toast.error(`${err}`);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const fileLabel = (url, uploadingNow) => {
    if (uploadingNow) return "Uploading…";
    if (url) return "Document uploaded. Choose a file to replace it.";
    return "JPEG, PNG, or PDF, max 5MB";
  };

  return (
    <Modal
      title="Update KYB"
      onSubmit={handleSave}
      onClose={() => {}}
      ref={modalRef}
      submitBtn="Save"
      cancelBtn="Close"
    >
      <div className="sm:w-[min(97vw,_560px)] text-slate-600">
        <p className="text-sm text-[#767676] mb-3">
          Required for merchant accounts. JPEG, PNG, or PDF, max 5MB.
          Registration and MD ID must not be expired. Proof of address must be
          dated within 90 days. MD name must match {user?.first_name}{" "}
          {user?.last_name}.
        </p>
        {error && <p className="text-sm text-[#FF0F00] mb-3">{error}</p>}

        <label className="block text-sm font-semibold mb-1">Status</label>
        <select
          aria-label="KYB status"
          className="w-full border rounded px-2 py-2 mb-3"
          value={form.status}
          onChange={(e) => updateField("status", e.target.value)}
          disabled={saving}
        >
          <option value="PENDING">PENDING</option>
          <option value="APPROVED">APPROVED</option>
          <option value="REJECTED">REJECTED</option>
        </select>

        <label className="block text-sm font-semibold mb-1">
          Business registration number
        </label>
        <input
          aria-label="Business registration number"
          className="w-full border rounded px-2 py-2 mb-3"
          value={form.business_registration_number}
          onChange={(e) => updateField("business_registration_number", e.target.value)}
          disabled={saving}
        />

        <label className="block text-sm font-semibold mb-1">
          Business registration expiry
        </label>
        <input
          aria-label="Business registration expiry"
          type="date"
          className="w-full border rounded px-2 py-2 mb-3"
          value={form.business_registration_expiry}
          onChange={(e) => updateField("business_registration_expiry", e.target.value)}
          disabled={saving}
        />

        <label className="block text-sm font-semibold mb-1">
          Business registration document
        </label>
        <p className="text-xs text-[#767676] mb-1">
          {fileLabel(form.business_registration_doc, uploading.business_registration_doc)}
        </p>
        {form.business_registration_doc && (
          <a
            href={form.business_registration_doc}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#405B9C] underline mb-1 inline-block"
          >
            View current document
          </a>
        )}
        <input
          aria-label="Business registration document"
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          className="w-full mb-3"
          onChange={(e) =>
            uploadKybDocument(e.target.files?.[0], "business_reg", "business_registration_doc")
          }
          disabled={saving || uploading.business_registration_doc}
        />

        <label className="block text-sm font-semibold mb-1">
          Selfie ID of MD — full name
        </label>
        <input
          aria-label="Selfie ID of MD — full name"
          className="w-full border rounded px-2 py-2 mb-3"
          value={form.md_name}
          onChange={(e) => updateField("md_name", e.target.value)}
          disabled={saving}
        />

        <label className="block text-sm font-semibold mb-1">MD ID expiry</label>
        <input
          aria-label="MD ID expiry"
          type="date"
          className="w-full border rounded px-2 py-2 mb-3"
          value={form.md_id_expiry}
          onChange={(e) => updateField("md_id_expiry", e.target.value)}
          disabled={saving}
        />

        <label className="block text-sm font-semibold mb-1">Selfie ID of MD</label>
        <p className="text-xs text-[#767676] mb-1">
          {fileLabel(form.md_selfie_doc, uploading.md_selfie_doc)}
        </p>
        {form.md_selfie_doc && (
          <a
            href={form.md_selfie_doc}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#405B9C] underline mb-1 inline-block"
          >
            View current document
          </a>
        )}
        <input
          aria-label="Selfie ID of MD"
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          className="w-full mb-3"
          onChange={(e) =>
            uploadKybDocument(e.target.files?.[0], "md_selfie", "md_selfie_doc")
          }
          disabled={saving || uploading.md_selfie_doc}
        />

        <label className="block text-sm font-semibold mb-1">
          Proof of address document date
        </label>
        <input
          aria-label="Proof of address document date"
          type="date"
          className="w-full border rounded px-2 py-2 mb-3"
          value={form.poa_document_date}
          onChange={(e) => updateField("poa_document_date", e.target.value)}
          disabled={saving}
        />

        <label className="block text-sm font-semibold mb-1">Proof of address</label>
        <p className="text-xs text-[#767676] mb-1">
          {fileLabel(form.proof_of_address_doc, uploading.proof_of_address_doc)}
        </p>
        {form.proof_of_address_doc && (
          <a
            href={form.proof_of_address_doc}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#405B9C] underline mb-1 inline-block"
          >
            View current document
          </a>
        )}
        <input
          aria-label="Proof of address"
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          className="w-full mb-3"
          onChange={(e) =>
            uploadKybDocument(e.target.files?.[0], "poa", "proof_of_address_doc")
          }
          disabled={saving || uploading.proof_of_address_doc}
        />

        <label className="block text-sm font-semibold mb-1">Tax ID</label>
        <input
          aria-label="Tax ID"
          className="w-full border rounded px-2 py-2 mb-3"
          value={form.tax_id}
          onChange={(e) => updateField("tax_id", e.target.value)}
          disabled={saving}
        />

        <BeatLoader
          color="#36d7b7"
          loading={saving || isUploading}
          cssOverride={{
            textAlign: "center",
            marginTop: "8px",
          }}
        />
      </div>
    </Modal>
  );
});

MerchantKybModal.displayName = "MerchantKybModal";

export default MerchantKybModal;
